import { isStopWork, latestFindings, type AssessmentFeedItem } from "@taskiq/shared";

/**
 * The last few things that happened at this site, from the check log.
 *
 * ── One row per assessment, not per visit ──────────────────────────────────
 *
 * A visit is a container; a check is the event. Deriving this from visits, as
 * it did first, meant a technician working through four modules produced one
 * line that changed its own detail as they went - so the feed said less the
 * busier the site got, and a stop-work raised on the second check was invisible
 * until the visit closed. Off `GET /sites/:id/assessments`, every check is its
 * own line the moment it exists.
 *
 * ── The kind is derived once, here ─────────────────────────────────────────
 *
 * The verb, the icon and the tone all come from it, never from parsing the
 * text - which is how a feed ends up congratulating somebody on a stop-work.
 * Findings are read off the row rather than counted server-side, because they
 * are already on it: an assessment carries its own `findings` array.
 */

export type ActivityKind = "stop-work" | "failed" | "attention" | "passed" | "running" | "skipped";

export interface Activity {
  id: string;
  kind: ActivityKind;
  /** The job, named the way the schedule names it. */
  title: string;
  /** Which check, and what it found. */
  detail: string;
  technician: { id: string | null; name: string; avatar: string | null };
  /**
   * ISO instant. Rendered relative to the READER's clock rather than the site's:
   * "twenty minutes ago" is a fact about the person holding the screen, and the
   * calendar is the one documented exception to that rule (decisions #25).
   */
  at: string;
  visitId: string;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

function kindOf(row: AssessmentFeedItem, stopWork: boolean): ActivityKind {
  if (row.status === "skipped") return "skipped";
  if (row.status !== "completed") return "running";
  if (stopWork) return "stop-work";
  if (row.outcome === "fail") return "failed";
  if (row.outcome === "attention") return "attention";
  return "passed";
}

export function activityFrom(rows: AssessmentFeedItem[], limit = 5): Activity[] {
  return (
    rows
      .map((row) => {
        // The newest attempt's, not every attempt's - a re-shot check has not
        // found its hazards twice.
        const findings = latestFindings(row.metadata);
        const stopWork = findings.some(isStopWork);
        const module = row.module?.label ?? "Check";

        const detail =
          row.status === "skipped"
            ? `${module} · skipped, not failed`
            : row.status !== "completed"
              ? `${module} · under way`
              : findings.length === 0
                ? `${module} · nothing found`
                : `${module} · ${plural(findings.length, "finding")}`;

        return {
          id: row.id,
          kind: kindOf(row, stopWork),
          title: row.visit?.appointment?.title ?? "Visit",
          detail,
          technician: {
            id: row.technician?.id ?? null,
            name: row.technician?.full_name ?? "Unknown",
            avatar: row.technician?.avatar_url ?? null,
          },
          // `completed_at` for a finished check and `created_at` for one that is
          // not: the feed is ordered by when the thing it describes happened, and
          // for a running check that is when it started.
          at: row.completed_at ?? row.started_at ?? row.created_at,
          visitId: row.visit_id,
        };
      })
      // ISO instants sort lexicographically, which is the whole reason this
      // product stores and passes them as strings.
      .sort((a, b) => b.at.localeCompare(a.at))
      .slice(0, limit)
  );
}
